import { AttendanceGrid } from '../components/AttendanceGrid.tsx'
import {
  notify,
  useAddGroupLink,
  useAddGroupMember,
  useAttendanceReport,
  useGroupResources,
  useRemoveGroupResource,
  useUploadGroupFile,
  groupFileUrl,
  useMeetings,
  usePatchMeeting,
  useArchiveGroup,
  useDecideGroupRequest,
  useDeleteGroup,
  useGroup,
  useGroupImage,
  useGroupTagsEditor,
  useUpdateGroupResource,
  GROUP_IMAGE_TYPES,
  useGroupRequests,
  useGroupRoster,
  useJoinGroup,
  useLeaveGroup,
  useOrganizationProfile,
  usePeopleSearch,
  useRemoveGroupMember,
  useSetGroupMemberRole,
  useUpdateGroup,
  type GroupResource,
  type Meeting,
  type RosterMember,
} from '@church/data'
import {
  describeSeries,
  expandSeries,
  WEEKDAYS,
  type GroupEnrollment,
  type GroupVisibility,
  type RecurringSeries,
} from '@church/domain'
import {
  faArrowLeft,
  faArrowRight,
  faArrowRotateLeft,
  faArrowUpRightFromSquare,
  faBoxArchive,
  faCalendarXmark,
  faCheck,
  faClipboardCheck,
  faEllipsis,
  faEnvelope,
  faEye,
  faFile,
  faFloppyDisk,
  faImage,
  faLink,
  faLock,
  faPaperclip,
  faPlus,
  faRightFromBracket,
  faTrashCan,
  faUser,
  faUserMinus,
  faUserPlus,
  faVideoDuotone,
  faXmark,
} from '@church/ui-icons'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  Input,
  ListHeader,
  listRow,
  PageHeader,
  PanelSection,
  Select,
  SidePanel,
  Spinner,
  ErrorState,
  ListSkeleton,
  Tabs,
  Textarea,
} from '../components/ui.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'
import { useTabParam } from '../lib/tabs.ts'
import { GroupPicture } from '../components/GroupPicture.tsx'
import { DisplayOptions } from '../components/DisplayOptions.tsx'
import { useListDisplay } from '../lib/display.ts'

/**
 * One group.
 *
 * Every affordance on this page comes from `group.access`, which the server
 * computed for this actor and this group. A leader of another group reaches
 * this page — the group is public — and sees a read-only card with no roster:
 * the roster query is not even sent, because a 403 is not an error state worth
 * rendering when the answer was known before asking.
 */
export const Route = createFileRoute('/_authed/_shell/groups/$id')({
  component: GroupScreen,
})

const TABS = ['Roster', 'Meetings', 'Files', 'Requests', 'Settings'] as const
type Tab = (typeof TABS)[number]

function GroupScreen() {
  const { id } = Route.useParams()
  const groupQuery = useGroup(id)
  const { data: group, isPending, error } = groupQuery
  // The tab is in the address: a reload or Back returns to it.
  const [tab, setTab] = useTabParam<Tab>(TABS)
  const join = useJoinGroup(id)
  useBreadcrumbs([{ label: group?.name ?? 'Group' }])

  if (isPending) return <ListSkeleton rows={5} />
  if (!group) {
    const status = (error as { status?: unknown } | null)?.status
    if (status !== 404 && status !== 403)
      return (
        <ErrorState
          error={error}
          safe="Nothing was changed: the group is safe on the server."
          retrying={groupQuery.isRefetching}
          onRetry={() => void groupQuery.refetch()}
        />
      )
    return (
      <EmptyState
        title="This group isn’t here"
        hint="It may have been deleted, or it may not be one you can see."
        action={
          <Link
            to="/groups"
            className="inline-flex items-center gap-2 rounded-control bg-brand-solid px-button-x py-button-y text-sm font-medium text-brand-on-solid no-underline hover:bg-brand-solid-hover"
          >
            <Icon icon={faArrowLeft} />
            All groups
          </Link>
        }
      />
    )
  }

  const visibleTabs = TABS.filter((name) => {
    if (name === 'Roster') return true
    if (name === 'Meetings') return true
    if (name === 'Files') return group.access.canViewResources
    if (name === 'Requests') return group.access.canManageMembers
    return group.access.canEdit || group.access.canArchive || group.access.canDelete
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title={group.name}
        facts={[group.groupTypeName, group.scheduleSummary, group.location]
          .filter(Boolean)
          .join(' · ')}
        action={
          (group.access.canJoin || group.access.canRequestToJoin) && (
            <Button
              icon={faUserPlus}
              pending={join.isPending}
              pendingLabel={group.access.canJoin ? 'Joining…' : 'Asking…'}
              onClick={() =>
                join.mutate(
                  {},
                  {
                    onSuccess: () =>
                      notify({
                        title: group.access.canJoin
                          ? `You joined ${group.name}`
                          : `Asked to join ${group.name}`,
                        description: group.access.canJoin
                          ? 'It’s in your groups now.'
                          : 'A leader will say yes or no; you’ll hear either way.',
                      }),
                  },
                )
              }
            >
              {group.access.canJoin ? 'Join group' : 'Ask to join'}
            </Button>
          )
        }
      />

      {group.archivedAt && (
        <Card className="bg-surface-sunken p-3 text-sm text-content-muted">
          This group is archived. Its history is kept, but it no longer appears in the directory.
        </Card>
      )}
      {group.hasImage && (
        <GroupPicture group={group} className="aspect-[3/1] w-full max-w-3xl rounded-xl" />
      )}
      {group.description && (
        <p className="max-w-2xl text-sm text-content-muted">{group.description}</p>
      )}
      <GroupContact group={group} />
      <ErrorNote error={join.error} onRetry={() => join.mutate({})} retrying={join.isPending} />

      <Tabs tabs={visibleTabs} value={tab} onChange={setTab}>
        {tab === 'Roster' && <RosterPanel group={group} />}
        {tab === 'Meetings' && <MeetingsPanel group={group} />}
        {tab === 'Files' && <ResourcesPanel group={group} />}
        {tab === 'Requests' && group.access.canManageMembers && <RequestsPanel groupId={id} />}
        {tab === 'Settings' && <SettingsPanel group={group} />}
      </Tabs>
    </div>
  )
}

type GroupDetail = NonNullable<ReturnType<typeof useGroup>['data']>

/** Its tags, who to ask, and — for its members — where it meets online. */
function GroupContact({ group }: { group: GroupDetail }) {
  if (group.tags.length === 0 && !group.contactEmail && !group.virtualUrl) return null
  return (
    <div className="flex max-w-3xl flex-wrap items-center gap-2">
      {group.tags.map((tag) => (
        <Chip key={tag.id}>{tag.name}</Chip>
      ))}
      {group.contactEmail && (
        <a
          href={`mailto:${group.contactEmail}`}
          className="inline-flex items-center gap-1.5 text-sm text-content-brand hover:underline"
        >
          <Icon icon={faEnvelope} className="size-3.5" />
          {group.contactEmail}
        </a>
      )}
      {group.virtualUrl && (
        <a
          href={group.virtualUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-content-brand hover:underline"
        >
          <Icon icon={faVideoDuotone} className="size-3.5" />
          Join online
        </a>
      )}
    </div>
  )
}

function RosterPanel({ group }: { group: GroupDetail }) {
  const { data: roster, isPending } = useGroupRoster(group.id, group.access.canViewRoster)
  const [adding, setAdding] = useState(false)
  // Whose panel is open; `shown` outlives closing so it can slide away.
  const [openId, setOpenId] = useState<string | null>(null)
  const [shownId, setShownId] = useState<string | null>(null)
  const manage = group.access.canManageMembers
  const display = useListDisplay('group-roster', ROSTER_FIELDS)

  if (!group.access.canViewRoster) {
    return (
      <EmptyState
        title="The roster is private"
        hint={
          group.relationship === 'none'
            ? 'Only this group’s leaders and church staff can see who is in it.'
            : 'This group’s leaders have chosen not to share its member list.'
        }
      />
    )
  }
  const shown = roster?.find((m) => m.membershipId === shownId)
  const leaders = roster?.filter((m) => m.role === 'leader').length ?? 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-content-muted">
          {roster
            ? `${roster.length} ${roster.length === 1 ? 'person' : 'people'}${leaders ? ` · ${leaders} ${leaders === 1 ? 'leader' : 'leaders'}` : ''}`
            : ''}
        </p>
        <div className="flex items-center gap-1">
          <DisplayOptions fields={ROSTER_FIELDS} shown={display.shown} onChange={display.set} />
          {manage && (
            <Button icon={faUserPlus} onClick={() => setAdding(true)}>
              Add
            </Button>
          )}
        </div>
      </div>

      {isPending ? (
        <Spinner />
      ) : !roster || roster.length === 0 ? (
        <EmptyState title="Nobody in this group yet" />
      ) : (
        <Card bleed>
          <ul className="divide-y divide-border-subtle">
            {roster.map((member) => (
              <li key={member.membershipId}>
                {manage ? (
                  <button
                    type="button"
                    onClick={() => {
                      setShownId(member.membershipId)
                      setOpenId(member.membershipId)
                    }}
                    className={`${listRow} flex w-full items-center gap-3 text-left hover:bg-surface-sunken`}
                  >
                    <MemberSummary member={member} hide={display.hide} />
                  </button>
                ) : (
                  <Link
                    to="/people/$id"
                    params={{ id: member.personId }}
                    className={`${listRow} flex items-center gap-3 no-underline hover:bg-surface-sunken`}
                  >
                    <MemberSummary member={member} hide={display.hide} />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {group.access.canLeave && <LeaveButton group={group} />}

      {manage && (
        <>
          <MemberPanel
            group={group}
            member={shown}
            open={openId !== null && shown !== undefined}
            onClose={() => setOpenId(null)}
          />
          <AddMemberPanel
            groupId={group.id}
            members={roster ?? []}
            open={adding}
            onClose={() => setAdding(false)}
          />
        </>
      )}
    </div>
  )
}

/** What a roster line shows on a phone; everything shows from `sm` up. */
const ROSTER_FIELDS = [
  { id: 'contact', label: 'Email', default: true },
  { id: 'role', label: 'Leader', default: true },
] as const
type RosterField = (typeof ROSTER_FIELDS)[number]['id']

/** A roster line: who, how to reach them, and whether they lead. */
function MemberSummary({
  member,
  hide,
}: {
  member: RosterMember
  hide: (field: RosterField) => string
}) {
  return (
    <>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-base font-medium text-content-default">
          {member.firstName} {member.lastName}
        </span>
        <span className={`block truncate text-sm text-content-muted ${hide('contact')}`}>
          {member.contactsHidden ? 'Contact hidden (minor)' : (member.primaryEmail ?? '')}
        </span>
      </span>
      {member.role === 'leader' && (
        <span className={hide('role')}>
          <Chip tone="brand">Leader</Chip>
        </span>
      )}
    </>
  )
}

/** One member: their role here, their profile, taking them off the roster. */
function MemberPanel({
  group,
  member,
  open,
  onClose,
}: {
  group: GroupDetail
  member: RosterMember | undefined
  open: boolean
  onClose: () => void
}) {
  const setRole = useSetGroupMemberRole(group.id)
  const remove = useRemoveGroupMember(group.id)
  const add = useAddGroupMember(group.id)
  if (!member) return null
  const name = `${member.firstName} ${member.lastName}`

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={`${member.firstName} ${member.lastName}`}
      description={
        member.contactsHidden ? 'Contact hidden (minor)' : (member.primaryEmail ?? undefined)
      }
      footer={
        <Button
          icon={faUserMinus}
          variant="quietDanger"
          className="-ml-4"
          pending={remove.isPending}
          pendingLabel="Removing…"
          onClick={() =>
            remove.mutate(member.personId, {
              onSuccess: () => {
                onClose()
                notify({
                  title: `Removed ${name} from ${group.name}`,
                  description: 'Their attendance history stays.',
                  action: {
                    label: 'Undo',
                    run: () => add.mutate({ personId: member.personId, role: member.role }),
                  },
                })
              },
            })
          }
        >
          Remove from group
        </Button>
      }
    >
      <PanelSection title="Role" hint="Leaders take attendance, manage the roster and share files.">
        <div className="grid grid-cols-2 gap-2">
          {(['member', 'leader'] as const).map((role) => (
            <button
              key={role}
              type="button"
              aria-pressed={member.role === role}
              disabled={setRole.isPending}
              onClick={() => {
                if (member.role !== role)
                  setRole.mutate(
                    { personId: member.personId, role },
                    {
                      onSuccess: () =>
                        notify({
                          title:
                            role === 'leader'
                              ? `${name} now leads ${group.name}`
                              : `${name} is a member of ${group.name}, not a leader`,
                          description:
                            role === 'leader'
                              ? 'They can take attendance, manage the roster and share files.'
                              : undefined,
                        }),
                    },
                  )
              }}
              className={`rounded-lg py-2 text-sm font-semibold transition-colors duration-(--duration-fast) ${
                member.role === role
                  ? 'bg-brand-solid text-brand-on-solid'
                  : 'border border-border-default text-content-default hover:bg-surface-sunken'
              }`}
            >
              {role === 'leader' ? 'Leader' : 'Member'}
            </button>
          ))}
        </div>
      </PanelSection>
      <ErrorNote error={setRole.error ?? remove.error} />
      <div className="border-t border-border-subtle pt-4">
        <Link
          to="/people/$id"
          params={{ id: member.personId }}
          className="flex items-center gap-3 rounded-lg px-1 py-2 text-sm font-medium text-content-brand hover:bg-surface-sunken"
        >
          <Icon icon={faUser} />
          <span className="flex-1">{member.firstName}’s profile</span>
          <Icon icon={faArrowRight} />
        </Link>
      </div>
    </SidePanel>
  )
}

/**
 * The shelf: what this group hands round.
 *
 * Visible to members even when the roster is not — a private group can keep
 * its member list to its leaders and still give everyone the week's reading.
 */
function ResourcesPanel({ group }: { group: GroupDetail }) {
  const { data: resources, isPending } = useGroupResources(group.id, group.access.canViewResources)
  const remove = useRemoveGroupResource(group.id)
  const [adding, setAdding] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [shownId, setShownId] = useState<string | null>(null)
  const manage = group.access.canManageResources

  if (isPending) return <Spinner />
  const shown = resources?.find((r) => r.id === shownId)

  return (
    <div className="space-y-4">
      {manage && (
        <div className="flex justify-end">
          <Button icon={faPaperclip} onClick={() => setAdding(true)}>
            Share
          </Button>
        </div>
      )}

      {!resources || resources.length === 0 ? (
        <EmptyState
          title="Nothing shared yet"
          hint="Study notes, a rota, a link to this week's video — whatever the group needs to hand round."
        />
      ) : (
        <Card bleed>
          <ul className="divide-y divide-border-subtle">
            {resources.map((resource) => (
              <li key={resource.id} className={`${listRow} flex items-center gap-3`}>
                <Icon
                  icon={resource.kind === 'file' ? faFile : faLink}
                  className="size-4 text-content-subtle"
                />
                <ResourceLink groupId={group.id} resource={resource} />
                {resource.visibility === 'leaders' && (
                  <Chip tone="warning">
                    <Icon icon={faLock} className="size-3" />
                    Leaders
                  </Chip>
                )}
                {manage && (
                  <IconButton
                    icon={faEllipsis}
                    label={`Manage ${resource.title}`}
                    className="ml-auto"
                    onClick={() => {
                      setShownId(resource.id)
                      setOpenId(resource.id)
                    }}
                  />
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {manage && (
        <>
          <SidePanel
            open={openId !== null && shown !== undefined}
            onOpenChange={(next) => {
              if (!next) setOpenId(null)
            }}
            title={shown?.title ?? ''}
            description={
              shown
                ? shown.kind === 'file'
                  ? `${shown.fileName ?? 'File'}${shown.sizeBytes ? ` · ${formatBytes(shown.sizeBytes)}` : ''}`
                  : hostOf(shown.url)
                : undefined
            }
            footer={
              shown && (
                <ConfirmButton
                  label={shown.kind === 'file' ? 'Delete file' : 'Remove link'}
                  icon={faTrashCan}
                  variant="quietDanger"
                  className="-ml-4"
                  title={`Remove “${shown.title}”?`}
                  description={
                    shown.kind === 'file'
                      ? 'The file is deleted from storage as well.'
                      : 'The link is removed from this group.'
                  }
                  confirmLabel="Remove"
                  onConfirm={() => remove.mutate(shown.id, { onSuccess: () => setOpenId(null) })}
                />
              )
            }
          >
            {shown?.description && (
              <PanelSection title="About">
                <p className="text-sm text-content-default">{shown.description}</p>
              </PanelSection>
            )}
            {shown && <ResourceVisibility groupId={group.id} resource={shown} />}
            {shown && (
              <a
                href={
                  shown.kind === 'link'
                    ? (shown.url ?? '#')
                    : groupFileUrl(import.meta.env.VITE_API_URL ?? '', group.id, shown.id)
                }
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center gap-3 rounded-lg px-1 py-2 text-sm font-medium text-content-brand hover:bg-surface-sunken"
              >
                <Icon icon={shown.kind === 'file' ? faFile : faLink} />
                <span className="flex-1">
                  {shown.kind === 'file' ? 'Open the file' : 'Open the link'}
                </span>
                <Icon icon={faArrowUpRightFromSquare} />
              </a>
            )}
            <ErrorNote error={remove.error} />
          </SidePanel>
          <AddResourcePanel groupId={group.id} open={adding} onClose={() => setAdding(false)} />
        </>
      )}
    </div>
  )
}

/** Who sees one thing on the shelf: everyone in the group, or its leaders. */
function ResourceVisibility({ groupId, resource }: { groupId: string; resource: GroupResource }) {
  const update = useUpdateGroupResource(groupId)
  return (
    <PanelSection title="Who sees it">
      <VisibilityChoice
        value={resource.visibility}
        disabled={update.isPending}
        onChange={(visibility) => update.mutate({ resourceId: resource.id, visibility })}
      />
      <ErrorNote error={update.error} />
    </PanelSection>
  )
}

function VisibilityChoice({
  value,
  disabled,
  onChange,
}: {
  value: 'members' | 'leaders'
  disabled?: boolean
  onChange: (value: 'members' | 'leaders') => void
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {(['members', 'leaders'] as const).map((option) => (
        <Button
          key={option}
          variant={value === option ? 'primary' : 'secondary'}
          aria-pressed={value === option}
          icon={option === 'members' ? faEye : faLock}
          disabled={disabled}
          onClick={() => {
            if (value !== option) onChange(option)
          }}
        >
          {option === 'members' ? 'Everyone' : 'Leaders only'}
        </Button>
      ))}
    </div>
  )
}

function ResourceLink({ groupId, resource }: { groupId: string; resource: GroupResource }) {
  const href =
    resource.kind === 'link'
      ? (resource.url ?? '#')
      : groupFileUrl(import.meta.env.VITE_API_URL ?? '', groupId, resource.id)

  return (
    <span className="min-w-0">
      <a
        href={href}
        // A link the group shared points off our origin: no referrer, and no
        // handle on this tab from whatever opens.
        target="_blank"
        rel="noreferrer noopener"
        className="text-sm text-content-brand hover:underline"
      >
        {resource.title}
      </a>
      <span className="ml-2 text-xs text-content-subtle">
        {resource.kind === 'file'
          ? `${resource.fileName ?? 'file'}${resource.sizeBytes ? ` · ${formatBytes(resource.sizeBytes)}` : ''}`
          : hostOf(resource.url)}
      </span>
      {resource.description && (
        <span className="block text-xs text-content-muted">{resource.description}</span>
      )}
    </span>
  )
}

/** A 25-byte file reading "0 KB" looks like a failed upload, so bytes stay bytes. */
const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/** The host, so somebody can see where a link goes before clicking it. */
function hostOf(url: string | null): string {
  if (!url) return ''
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

function AddResourcePanel({
  groupId,
  open,
  onClose,
}: {
  groupId: string
  open: boolean
  onClose: () => void
}) {
  const addLink = useAddGroupLink(groupId)
  const upload = useUploadGroupFile(groupId)
  const [mode, setMode] = useState<'link' | 'file'>('link')
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [visibility, setVisibility] = useState<'members' | 'leaders'>('members')
  // A fresh form each time it opens (it stays mounted, so it can slide out).
  const [openedFor, setOpenedFor] = useState(open)
  if (open !== openedFor) {
    setOpenedFor(open)
    if (open) {
      setMode('link')
      setTitle('')
      setUrl('')
      setFile(null)
      setVisibility('members')
    }
  }
  const close = () => {
    addLink.reset()
    upload.reset()
    onClose()
  }

  const submit = () => {
    if (mode === 'link') {
      addLink.mutate({ title: title.trim(), url: url.trim(), visibility }, { onSuccess: close })
      return
    }
    if (!file) return
    upload.mutate(
      {
        title: title.trim() === '' ? file.name : title.trim(),
        file,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        visibility,
      },
      { onSuccess: close },
    )
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) close()
      }}
      title="Share with the group"
      primary={
        <Button
          icon={mode === 'link' ? faLink : faPaperclip}
          className="py-1.5"
          pending={addLink.isPending || upload.isPending}
          pendingLabel={upload.isPending ? 'Uploading…' : undefined}
          disabled={mode === 'link' ? title.trim() === '' || url.trim() === '' : file === null}
          onClick={submit}
        >
          {mode === 'link' ? 'Share' : 'Upload'}
        </Button>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        {(['link', 'file'] as const).map((option) => (
          <Button
            key={option}
            variant={mode === option ? 'primary' : 'secondary'}
            aria-pressed={mode === option}
            icon={option === 'link' ? faLink : faFile}
            onClick={() => setMode(option)}
          >
            {option === 'link' ? 'Link' : 'File'}
          </Button>
        ))}
      </div>

      <div className="space-y-3">
        <Field label="Name" description="What the group will look for.">
          <Input
            placeholder="This week’s reading"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>
        {mode === 'link' ? (
          <Field label="Link">
            <Input
              placeholder="https://…"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
            />
          </Field>
        ) : (
          <Field label="File" description="Up to 25MB. Documents, images, audio and video.">
            <input
              type="file"
              className="block w-full text-sm text-content-muted file:mr-3 file:rounded-lg file:border-0 file:bg-surface-sunken file:px-3 file:py-2 file:text-sm"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </Field>
        )}
      </div>

      <PanelSection title="Who sees it" hint="Leaders only: its leaders and church staff.">
        <VisibilityChoice value={visibility} onChange={setVisibility} />
      </PanelSection>

      <ErrorNote error={addLink.error ?? upload.error} />
    </SidePanel>
  )
}

/**
 * Meetings: the schedule, what happened at each one, and the attendance grid.
 *
 * The list mixes rows that exist with rows that do not — a meeting is only
 * written down once something happens to it — which is why every action here
 * is addressed by date rather than by id.
 */
function MeetingsPanel({ group }: { group: GroupDetail }) {
  const [taking, setTaking] = useState<string | null>(null)
  const { data: meetings, isPending } = useMeetings(group.id)
  const patch = usePatchMeeting(group.id)

  if (isPending) return <Spinner />
  if (!meetings || meetings.length === 0) {
    return (
      <EmptyState
        title="No meetings"
        hint="Set a schedule under Settings and the meetings appear here automatically."
      />
    )
  }

  const now = Date.now()
  const upcoming = meetings.filter((meeting) => new Date(meeting.endsAt).getTime() >= now)
  const past = meetings.filter((meeting) => new Date(meeting.endsAt).getTime() < now).reverse()

  return (
    <div className="space-y-6">
      {taking && <AttendanceGrid groupId={group.id} date={taking} onDone={() => setTaking(null)} />}

      {group.access.canViewRoster && <AttendanceSummaryCard groupId={group.id} />}

      <MeetingList
        title="Coming up"
        meetings={upcoming.slice(0, 8)}
        group={group}
        onTake={setTaking}
        onCancel={(date, cancelled) =>
          patch.mutate({ date, status: cancelled ? 'scheduled' : 'cancelled' })
        }
      />
      <MeetingList
        title="Past meetings"
        meetings={past.slice(0, 20)}
        group={group}
        onTake={setTaking}
        onCancel={(date, cancelled) =>
          patch.mutate({ date, status: cancelled ? 'scheduled' : 'cancelled' })
        }
      />
      <ErrorNote error={patch.error} />
    </div>
  )
}

function MeetingList({
  title,
  meetings,
  group,
  onTake,
  onCancel,
}: {
  title: string
  meetings: Meeting[]
  group: GroupDetail
  onTake: (date: string) => void
  onCancel: (date: string, cancelled: boolean) => void
}) {
  if (meetings.length === 0) return null

  return (
    <Card bleed>
      <ListHeader>{title}</ListHeader>
      <ul className="divide-y divide-border-subtle">
        {meetings.map((meeting) => {
          const cancelled = meeting.status === 'cancelled'
          return (
            <li key={meeting.occurrenceDate} className="flex flex-wrap items-center gap-3 p-3">
              <span className="min-w-40">
                <span
                  className={`text-sm ${cancelled ? 'text-content-subtle line-through' : 'text-content-default'}`}
                >
                  {new Date(meeting.startsAt).toLocaleDateString(undefined, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  })}
                </span>
                <span className="ml-2 text-xs text-content-subtle">
                  {new Date(meeting.startsAt).toLocaleTimeString(undefined, {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
              </span>

              <span className="text-xs text-content-muted">
                {cancelled
                  ? (meeting.note ?? 'Cancelled')
                  : meeting.attendanceTaken
                    ? `${meeting.present} present${meeting.absent > 0 ? `, ${meeting.absent} absent` : ''}`
                    : meeting.source === 'ad-hoc'
                      ? 'One-off meeting'
                      : 'Not recorded'}
              </span>

              {group.access.canManageMembers && (
                <span className="ml-auto flex shrink-0 gap-1">
                  {!cancelled && (
                    <Button
                      variant="ghost"
                      icon={faClipboardCheck}
                      className="px-2 py-1 text-xs"
                      onClick={() => onTake(meeting.occurrenceDate)}
                    >
                      {meeting.attendanceTaken ? 'Edit attendance' : 'Attendance'}
                    </Button>
                  )}
                  <IconButton
                    icon={cancelled ? faArrowRotateLeft : faCalendarXmark}
                    label={cancelled ? 'Reinstate meeting' : 'Cancel meeting'}
                    onClick={() => onCancel(meeting.occurrenceDate, cancelled)}
                  />
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </Card>
  )
}

/** The numbers a pastor asks for, and the names behind the worrying one. */
function AttendanceSummaryCard({ groupId }: { groupId: string }) {
  const { data: report } = useAttendanceReport(groupId)
  if (!report || report.meetingsRecorded === 0) return null

  return (
    <Card className="space-y-3 p-4">
      <div className="flex flex-wrap gap-6">
        <Stat label="Average attendance" value={String(report.averageAttendance)} />
        <Stat
          label="Attendance rate"
          value={report.attendanceRate === null ? '—' : `${report.attendanceRate}%`}
        />
        <Stat
          label="Meetings recorded"
          value={`${report.meetingsRecorded} of ${report.meetingsHeld}`}
        />
      </div>

      {report.missing.length > 0 && (
        <div className="border-t border-border-subtle pt-3">
          <p className="text-xs font-medium uppercase text-content-subtle">Not seen for a while</p>
          <ul className="mt-1 space-y-0.5">
            {report.missing.map((person) => (
              <li key={person.personId} className="text-sm text-content-muted">
                <Link
                  to="/people/$id"
                  params={{ id: person.personId }}
                  className="text-content-brand hover:underline"
                >
                  {person.firstName} {person.lastName}
                </Link>{' '}
                — missed {person.missedMeetings}
                {person.lastPresentOn ? `, last here ${person.lastPresentOn}` : ', never recorded'}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xl font-semibold text-content-default">{value}</p>
      <p className="text-xs text-content-muted">{label}</p>
    </div>
  )
}

/**
 * Rendered only when the actor is actually in the group: this asks the API who
 * the signed-in person is, and a staff member browsing somebody else's group
 * has no reason to make that request.
 */
function LeaveButton({ group }: { group: GroupDetail }) {
  const leave = useLeaveGroup(group.id)
  if (!leave.isReady) return null

  return (
    <>
      <ConfirmButton
        label="Leave this group"
        icon={faRightFromBracket}
        variant="quietDanger"
        title={`Leave ${group.name}?`}
        description={
          group.relationship === 'leader'
            ? 'You lead this group — leaving gives up that role as well.'
            : 'You can ask to join again later.'
        }
        confirmLabel="Leave group"
        onConfirm={leave.leave}
      />
      <ErrorNote error={leave.error} />
    </>
  )
}

function MemberName({ member }: { member: RosterMember }) {
  return (
    <span className="min-w-0">
      <Link
        to="/people/$id"
        params={{ id: member.personId }}
        className="text-sm text-content-brand hover:underline"
      >
        {member.firstName} {member.lastName}
      </Link>
      {member.contactsHidden && (
        <span className="block truncate text-xs text-content-subtle sm:ml-2 sm:inline">
          Contact hidden (minor)
        </span>
      )}
      {member.primaryEmail && (
        <span className="block truncate text-xs text-content-subtle sm:ml-2 sm:inline">
          {member.primaryEmail}
        </span>
      )}
    </span>
  )
}

/** Adding people: search the directory; the panel stays open to add several. */
function AddMemberPanel({
  groupId,
  members,
  open,
  onClose,
}: {
  groupId: string
  members: RosterMember[]
  open: boolean
  onClose: () => void
}) {
  const add = useAddGroupMember(groupId)
  const [query, setQuery] = useState('')
  const search = usePeopleSearch(query)
  const inGroup = (personId: string) => members.some((m) => m.personId === personId)

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose()
          setQuery('')
        }
      }}
      title="Add someone"
      description="They join as a member; make them a leader from their panel."
    >
      <Input
        placeholder="Search the directory…"
        aria-label="Search the directory"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {search.results.length > 0 && (
        <ul className="divide-y divide-border-subtle">
          {search.results.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                disabled={add.isPending || inGroup(person.id)}
                onClick={() => add.mutate({ personId: person.id })}
                className="flex w-full items-center justify-between gap-2 px-1 py-3 text-left text-sm enabled:hover:bg-surface-sunken"
              >
                <span className="text-content-default">
                  {person.firstName} {person.lastName}
                </span>
                {inGroup(person.id) ? (
                  <span className="text-content-muted">In the group</span>
                ) : (
                  <Icon icon={faPlus} label="Add" className="size-4 text-content-brand" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      <ErrorNote error={add.error} />
    </SidePanel>
  )
}

function RequestsPanel({ groupId }: { groupId: string }) {
  const { data: requests, isPending } = useGroupRequests(groupId)
  const decide = useDecideGroupRequest(groupId)

  if (isPending) return <Spinner />
  if (!requests || requests.length === 0) {
    return <EmptyState title="No pending requests" hint="People who ask to join appear here." />
  }

  return (
    <Card bleed>
      <ul className="divide-y divide-border-subtle">
        {requests.map((request) => (
          <li key={request.membershipId} className="space-y-2 p-3">
            <div className="flex items-center gap-3">
              <MemberName member={request} />
              <span className="ml-auto flex shrink-0 gap-1">
                <Button
                  variant="success"
                  icon={faCheck}
                  className="px-3 py-1.5"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ personId: request.personId, decision: 'approve' })}
                >
                  Approve
                </Button>
                <IconButton
                  icon={faXmark}
                  label="Decline"
                  variant="danger"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ personId: request.personId, decision: 'decline' })}
                />
              </span>
            </div>
            {request.requestNote && (
              <p className="text-xs text-content-muted">“{request.requestNote}”</p>
            )}
          </li>
        ))}
      </ul>
      <ErrorNote error={decide.error} />
    </Card>
  )
}

function SettingsPanel({ group }: { group: GroupDetail }) {
  const update = useUpdateGroup(group.id)
  const archive = useArchiveGroup(group.id)
  const remove = useDeleteGroup()
  const navigate = useNavigate()

  const [form, setForm] = useState({
    name: group.name,
    description: group.description ?? '',
    location: group.location ?? '',
    capacity: group.capacity === null ? '' : String(group.capacity),
    contactEmail: group.contactEmail ?? '',
    virtualUrl: group.virtualUrl ?? '',
  })

  return (
    <div className="space-y-4">
      {group.access.canEdit && (
        <Card className="space-y-3 p-4 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name">
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Field>
            <Field label="Meets at">
              <Input
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              />
            </Field>
            <Field label="Capacity" description="Blank means no limit.">
              <Input
                type="number"
                min={1}
                value={form.capacity}
                onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
              />
            </Field>
            <Field label="Contact email" description="Who to ask about the group.">
              <Input
                type="email"
                value={form.contactEmail}
                onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
              />
            </Field>
            <Field
              label="Online meeting link"
              description="Shown to members, and to anyone when the group is open to join."
            >
              <Input
                type="url"
                placeholder="https://…"
                value={form.virtualUrl}
                onChange={(e) => setForm((f) => ({ ...f, virtualUrl: e.target.value }))}
              />
            </Field>
          </div>
          <Field label="Description">
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Field>
          <ErrorNote error={update.error} />
          <Button
            icon={faFloppyDisk}
            disabled={update.isPending || form.name.trim() === ''}
            onClick={() =>
              update.mutate({
                name: form.name.trim(),
                description: form.description.trim() === '' ? null : form.description.trim(),
                location: form.location.trim() === '' ? null : form.location.trim(),
                capacity: form.capacity === '' ? null : Number(form.capacity),
                contactEmail: form.contactEmail.trim() === '' ? null : form.contactEmail.trim(),
                virtualUrl: form.virtualUrl.trim() === '' ? null : form.virtualUrl.trim(),
              })
            }
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </Card>
      )}

      {group.access.canEdit && <PictureCard group={group} />}
      {group.access.canEdit && <TagsCard group={group} />}
      {group.ownSettings && <PrivacyCard group={group} />}
      {group.access.canEdit && <ScheduleCard group={group} />}

      {(group.access.canArchive || group.access.canDelete) && (
        <Card className="flex flex-wrap items-center gap-3 p-4 sm:p-6">
          {group.access.canArchive && (
            <Button
              variant="secondary"
              icon={group.archivedAt === null ? faBoxArchive : faArrowRotateLeft}
              disabled={archive.isPending}
              onClick={() => archive.mutate(group.archivedAt === null)}
            >
              {group.archivedAt === null ? 'Archive group' : 'Reopen group'}
            </Button>
          )}
          {group.access.canDelete && (
            <ConfirmButton
              label="Delete group"
              icon={faTrashCan}
              variant="quietDanger"
              title={`Delete ${group.name}?`}
              description="The roster and everything recorded against this group goes with it. Archiving keeps the history instead."
              confirmLabel="Delete group"
              onConfirm={() =>
                remove.mutate(group.id, { onSuccess: () => void navigate({ to: '/groups' }) })
              }
            />
          )}
          <ErrorNote error={archive.error ?? remove.error} />
        </Card>
      )}
    </div>
  )
}

/** The group's picture: shown on its page and in the directory. */
function PictureCard({ group }: { group: GroupDetail }) {
  const { upload, remove } = useGroupImage(group.id)
  return (
    <Card className="space-y-3 p-4 sm:p-6">
      <h2 className="font-semibold text-content-default">Picture</h2>
      <GroupPicture group={group} className="aspect-[3/1] w-full max-w-md rounded-xl" />
      <div className="flex flex-wrap items-center gap-2">
        <label
          className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border-default bg-surface-raised px-4 py-2 text-sm font-medium text-content-default hover:bg-surface-sunken ${upload.isPending ? 'pointer-events-none opacity-50' : ''}`}
        >
          <Icon icon={faImage} />
          {upload.isPending ? 'Uploading…' : group.hasImage ? 'Replace picture' : 'Upload picture'}
          <input
            type="file"
            accept={GROUP_IMAGE_TYPES.join(',')}
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) upload.mutate({ type: file.type, body: file })
              event.target.value = ''
            }}
          />
        </label>
        {group.hasImage && (
          <IconButton
            icon={faTrashCan}
            label="Remove picture"
            variant="quietDanger"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          />
        )}
      </div>
      <p className="text-xs text-content-muted">
        JPEG, PNG or WebP, up to 10 MB. Wide pictures look best.
      </p>
      <ErrorNote error={upload.error ?? remove.error} />
    </Card>
  )
}

/** Its tags, by category: what the directory's filters find it by. */
function TagsCard({ group }: { group: GroupDetail }) {
  const editor = useGroupTagsEditor(
    group.id,
    group.tags.map((tag) => tag.id),
  )
  if (editor.facets.length === 0) return null
  return (
    <Card className="space-y-4 p-4 sm:p-6">
      <h2 className="font-semibold text-content-default">Tags</h2>
      {editor.facets.map((facet) => (
        <PanelSection
          key={facet.id}
          title={facet.name}
          hint={facet.multiple ? undefined : 'One per group.'}
        >
          {facet.tags.length === 0 ? (
            <p className="text-sm text-content-muted">No tags in this category yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {facet.tags.map((tag) => {
                const on = editor.selected.includes(tag.id)
                return (
                  <button
                    key={tag.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => editor.toggle(tag.id)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                      on
                        ? 'bg-brand-solid text-brand-on-solid'
                        : 'border border-border-default text-content-muted hover:bg-surface-sunken'
                    }`}
                  >
                    {tag.name}
                  </button>
                )
              })}
            </div>
          )}
        </PanelSection>
      ))}
      <ErrorNote error={editor.save.error} />
      <div className="flex gap-2">
        <Button
          icon={faFloppyDisk}
          disabled={!editor.dirty || editor.save.isPending}
          onClick={() => editor.save.mutate(editor.selected)}
        >
          {editor.save.isPending ? 'Saving…' : 'Save tags'}
        </Button>
        {editor.dirty && (
          <Button variant="ghost" icon={faXmark} onClick={editor.reset}>
            Undo
          </Button>
        )}
      </div>
    </Card>
  )
}

const VISIBILITY_WORDS: Record<GroupVisibility, string> = {
  public: 'Anyone in the church can find it',
  members: 'Only its members see it',
  private: 'Staff and its leaders only',
}
const ENROLLMENT_WORDS: Record<GroupEnrollment, string> = {
  open: 'Anyone can join straight away',
  request: 'People ask, a leader approves',
  invite: 'Invitation only',
}

/**
 * Its own privacy, for staff: each setting follows the type unless this
 * group says otherwise.
 */
function PrivacyCard({ group }: { group: GroupDetail }) {
  const update = useUpdateGroup(group.id)
  const own = group.ownSettings
  if (!own) return null
  // What the type says is what applies wherever the group has no setting.
  const inherited = (key: 'visibility' | 'enrollment' | 'rosterVisibleToMembers') =>
    own[key] === null

  return (
    <Card className="space-y-3 p-4 sm:p-6">
      <div>
        <h2 className="font-semibold text-content-default">Privacy</h2>
        <p className="text-xs text-content-muted">
          Each follows the {group.groupTypeName} type unless set here.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Who can find it">
          <Select
            value={own.visibility ?? ''}
            disabled={update.isPending}
            onChange={(e) =>
              update.mutate({
                visibility: e.target.value === '' ? null : (e.target.value as GroupVisibility),
              })
            }
          >
            <option value="">
              As the type
              {inherited('visibility')
                ? ` (${VISIBILITY_WORDS[group.settings.visibility].toLowerCase()})`
                : ''}
            </option>
            {(Object.keys(VISIBILITY_WORDS) as GroupVisibility[]).map((value) => (
              <option key={value} value={value}>
                {VISIBILITY_WORDS[value]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="How people join">
          <Select
            value={own.enrollment ?? ''}
            disabled={update.isPending}
            onChange={(e) =>
              update.mutate({
                enrollment: e.target.value === '' ? null : (e.target.value as GroupEnrollment),
              })
            }
          >
            <option value="">
              As the type
              {inherited('enrollment')
                ? ` (${ENROLLMENT_WORDS[group.settings.enrollment].toLowerCase()})`
                : ''}
            </option>
            {(Object.keys(ENROLLMENT_WORDS) as GroupEnrollment[]).map((value) => (
              <option key={value} value={value}>
                {ENROLLMENT_WORDS[value]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Roster">
          <Select
            value={own.rosterVisibleToMembers === null ? '' : String(own.rosterVisibleToMembers)}
            disabled={update.isPending}
            onChange={(e) =>
              update.mutate({
                rosterVisibleToMembers: e.target.value === '' ? null : e.target.value === 'true',
              })
            }
          >
            <option value="">
              As the type
              {inherited('rosterVisibleToMembers')
                ? group.settings.rosterVisibleToMembers
                  ? ' (members see it)'
                  : ' (leaders only)'
                : ''}
            </option>
            <option value="true">Members see who is in it</option>
            <option value="false">Leaders and staff only</option>
          </Select>
        </Field>
      </div>
      <ErrorNote error={update.error} />
    </Card>
  )
}

/**
 * The meeting schedule.
 *
 * Stored as a wall time plus an IANA zone, never as a UTC instant: "Tuesdays at
 * 7pm" must stay 7pm for the people in the room when the clocks change. The
 * preview below is the real expander from @church/domain, so what is shown here
 * is what the server will generate meetings from.
 */
function ScheduleCard({ group }: { group: GroupDetail }) {
  const update = useUpdateGroup(group.id)
  const { data: org } = useOrganizationProfile()
  const existing = group.schedule as RecurringSeries | null

  const [draft, setDraft] = useState(() => ({
    date: existing?.start.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
    time: existing?.start.slice(11, 16) ?? '19:00',
    durationMinutes: String(existing?.durationMinutes ?? 90),
    freq: existing?.rule.freq ?? ('WEEKLY' as const),
    interval: String(existing?.rule.interval ?? 1),
    byDay: (existing?.rule.byDay ?? [])
      .map((entry) => (typeof entry === 'string' ? entry : entry.day))
      .join(','),
  }))

  const timeZone = existing?.timeZone ?? org?.timezone ?? 'UTC'
  const series: RecurringSeries = {
    start: `${draft.date}T${draft.time}`,
    durationMinutes: Number(draft.durationMinutes) || 60,
    timeZone,
    rule: {
      freq: draft.freq,
      ...(Number(draft.interval) > 1 ? { interval: Number(draft.interval) } : {}),
      ...(draft.byDay === ''
        ? {}
        : { byDay: draft.byDay.split(',').filter(Boolean) as RecurringSeries['rule']['byDay'] }),
    },
  }

  const preview = safeExpand(series)

  return (
    <Card className="space-y-3 p-4 sm:p-6">
      <h2 className="font-semibold text-content-default">Schedule</h2>

      <div className="grid gap-3 sm:grid-cols-4">
        <Field label="First meeting">
          <Input
            type="date"
            value={draft.date}
            onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
          />
        </Field>
        <Field label="Time">
          <Input
            type="time"
            value={draft.time}
            onChange={(e) => setDraft((d) => ({ ...d, time: e.target.value }))}
          />
        </Field>
        <Field label="Minutes">
          <Input
            type="number"
            min={15}
            step={15}
            value={draft.durationMinutes}
            onChange={(e) => setDraft((d) => ({ ...d, durationMinutes: e.target.value }))}
          />
        </Field>
        <Field label="Repeats">
          <Select
            value={draft.freq}
            onChange={(e) =>
              setDraft((d) => ({ ...d, freq: e.target.value as typeof draft.freq, byDay: '' }))
            }
          >
            <option value="WEEKLY">Weekly</option>
            <option value="MONTHLY">Monthly</option>
            <option value="DAILY">Daily</option>
          </Select>
        </Field>
      </div>

      {draft.freq === 'WEEKLY' && (
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((day) => {
            const selected = draft.byDay.split(',').includes(day)
            return (
              <button
                key={day}
                type="button"
                className={`rounded-full px-3 py-1 text-xs ${
                  selected
                    ? 'bg-brand-soft text-content-brand'
                    : 'bg-surface-sunken text-content-muted'
                }`}
                onClick={() =>
                  setDraft((d) => {
                    const days = d.byDay.split(',').filter(Boolean)
                    const next = selected ? days.filter((v) => v !== day) : [...days, day]
                    return { ...d, byDay: next.join(',') }
                  })
                }
              >
                {day}
              </button>
            )
          })}
          <span className="self-center text-xs text-content-subtle">
            Leave blank to repeat on the first meeting’s weekday.
          </span>
        </div>
      )}

      <div className="rounded-lg bg-surface-sunken p-3 text-sm">
        <p className="font-medium text-content-default">{describeSeries(series)}</p>
        <p className="text-xs text-content-muted">Times are {timeZone}.</p>
        <ul className="mt-2 space-y-0.5 text-xs text-content-muted">
          {preview.map((occurrence) => (
            <li key={occurrence.localStart}>
              {new Date(occurrence.startsAt).toLocaleString(undefined, { timeZone })}
            </li>
          ))}
        </ul>
      </div>

      <ErrorNote error={update.error} />
      <div className="flex gap-2">
        <Button
          icon={faFloppyDisk}
          disabled={update.isPending}
          onClick={() => update.mutate({ schedule: series })}
        >
          Save schedule
        </Button>
        {existing && (
          <Button
            variant="ghost"
            icon={faXmark}
            disabled={update.isPending}
            onClick={() => update.mutate({ schedule: null })}
          >
            Clear schedule
          </Button>
        )}
      </div>
    </Card>
  )
}

/** A half-typed date is a normal state of a form, not an error to surface. */
function safeExpand(series: RecurringSeries) {
  try {
    return expandSeries(series, { limit: 3 })
  } catch {
    return []
  }
}
